/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Drawer as NativeDrawer,
	Text,
	Button,
	Stack,
	Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Style
import classes from './DrawerUserShowMore.module.css';

// Utils
import { truncateString } from '@utils/functions';

// Icons

import { IconEdit, IconTrash } from '@tabler/icons-react';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TParsedUser } from '../../types';
import isNil from 'lodash/isNil';
import { LanguageDisplay } from '@components/atoms/LanguageDisplay';

import AvatarImage from '@assets/images/eskimo.png';
import { VerifiedButton } from '@components/atoms';
import { useDisableUser } from '../../api/disable-user';
import { useEnableUser } from '../../api/enable-user';
import useStore from '@store/index';
import { queryClient } from '@lib/react-query';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type DrawerMeterPointShowMoreProps = {
	onClose: () => void;
	handledelete?: () => void;
	handleupdate?: () => void;
	opened: boolean;
	data: Partial<TParsedUser>;
	position: 'right' | 'left';
	fromstepper?: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerUserShowMore = (
	props: DrawerMeterPointShowMoreProps
) => {
	const {
		opened,
		onClose,
		handledelete,
		fromstepper,
		data: user,
		handleupdate,
		...restProps
	} = props;

	/* ********************************  HOOKS  ********************************* */
	const {
		usersPage,
		usersPageSize,
		setUsersSelectedRow,
		usersSelectedRow,
	} = useStore();

	const { t } = useTranslation();
	const { checkAcl } = useAuthorization();
	const disableUser = useDisableUser({
		mutationConfig: {
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: [
						'users',
						{ page: usersPage, size: usersPageSize },
					],
				});
				setUsersSelectedRow({
					...usersSelectedRow,
					disabled: !usersSelectedRow.disabled,
				});
			},
		},
	});

	const enableUser = useEnableUser({
		mutationConfig: {
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: [
						'users',
						{ page: usersPage, size: usersPageSize },
					],
				});
				setUsersSelectedRow({
					...usersSelectedRow,
					disabled: !usersSelectedRow.disabled,
				});
			},
		},
	});

	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	/* ******************************** RENDERING ******************************* */
	if (!isNil(user)) {
		const {
			organizationName,
			createdAt,
			updatedAt,
			avatar,
			role,
			language,
			email,
			phoneNumber,
			firstName,
			lastName,
			disabled,
			lastLoginAt,
		} = user;

		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<img
						className={classes.logo}
						src={avatar || AvatarImage}
					/>

					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.user')}
						</Text>
						{firstName && lastName && (
							<Text className={classes.customTitle}>
								{`${firstName} ${lastName}`}
							</Text>
						)}
					</Box>
				</Box>
				{/* Body */}
				<Box className={classes.bodyDrawer}>
					<Box className={classes.row}>
						<Text className={classes.label}>
							{t('global.status')}
						</Text>

						<Text className={classes.value}>
							<VerifiedButton
								data-testid="P2DlDXR7_Z5jdHi6B-button"
								isverified={!disabled}
								onClick={() => {
									if (props.data?.id) {
										if (!disabled) {
											disableUser.mutate(props.data?.id);
										} else {
											enableUser.mutate(props.data?.id);
										}
									}
								}}
							>
								{disabled
									? t('global.inactive')
									: t('global.active')}
							</VerifiedButton>
						</Text>
					</Box>

					{organizationName && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={organizationName}
								disabled={organizationName.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(organizationName, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{!isNil(language) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.language')}
							</Text>
							<LanguageDisplay
								language={language || 'fr'}
							/>
						</Box>
					)}

					{!isNil(role) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.role')}
							</Text>

							<Text className={classes.value}>
								{t(`roles.${role}`)}
							</Text>
						</Box>
					)}

					{!isNil(email) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.contactEmail')}
							</Text>
							<Tooltip
								label={email}
								disabled={email.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(email, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{!isNil(phoneNumber) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.contactPhone')}
							</Text>
							<Tooltip
								label={phoneNumber}
								disabled={phoneNumber.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(phoneNumber, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{!isNil(createdAt) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.createdAt')}
							</Text>
							<Tooltip
								label={createdAt}
								disabled={createdAt.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(createdAt, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{!isNil(updatedAt) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.updatedAt')}
							</Text>
							<Text className={classes.value}>
								{updatedAt}
							</Text>
						</Box>
					)}

					{!isNil(lastLoginAt) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.lastLoginAt')}
							</Text>
							<Text className={classes.value}>
								{lastLoginAt}
							</Text>
						</Box>
					)}
				</Box>

				{/* Footer */}
				<Stack gap={'10px'} className={classes.footer}>
					{checkAcl(ACLS.USERS.UPDATE) && (
						<Button
							onClick={handleupdate}
							leftSection={<IconEdit size={16} />}
							size={isBigScreen ? 'md' : 'sm'}
							fullWidth
							classNames={{
								root: classes.customDeleteButton,
							}}
						>
							{t('users.updateUser')}
						</Button>
					)}
					{checkAcl(ACLS.USERS.DELETE) && (
						<Button
							onClick={handledelete}
							leftSection={<IconTrash size={16} />}
							size={isBigScreen ? 'md' : 'sm'}
							fullWidth
							classNames={{
								root: classes.customDeleteButton,
							}}
						>
							{t('users.deleteUser')}
						</Button>
					)}
				</Stack>
			</NativeDrawer>
		);
	}
};
